import React from 'react';

// @ts-ignore
import AuthActions from 'auth/AuthActions';
import Box from 'components/Box';
import BoxBody from 'components/BoxBody';
import Button from 'components/Button';
import CoachingHeading from 'coaching/components/CoachingHeading';
import { consume } from 'lib/queryParams';
import Container from 'components/Container';
import Columns2 from 'components/Columns2';
import Display from 'components/Display';
import EventTracking from 'lib/EventTracking';
import Heading from 'components/Heading';
import Image from 'components/Image';
import MediaObject from 'components/MediaObject';
import Row from 'components/Row';
import SwirlyDown from 'assets/images/coaching/swirly_down.svg';
import Text from 'components/Text';
import TextWithIcon from 'components/TextWithIcon';
import { browserHistory } from 'react-router';

type Props = {
  onContinue: () => void;
};

type State = {
  isSignedIn: boolean;
  loading: boolean;
};

class CoachingSelfServeIntroView extends React.Component<Props, State> {
  state: State = {
    isSignedIn: false,
    loading: true,
  };

  async componentDidMount() {
    const user = await AuthActions.checkLoggedInAndLoadAppData().catch(() =>
      this.setState({ loading: false })
    );

    this.setState({ isSignedIn: !!user, loading: false }, () => {
      if (!this.state.isSignedIn) {
        // if the user is not signed in, we should try and parse the
        // user ID from the URL if we have it, in order to maintain
        // consistency in our data pipeline across the entire user flow
        const params = consume(browserHistory, ['user_id']);
        const userId = params.user_id;
        EventTracking.setUserInfo(userId);
      }
    });

    this.trackEvent('view');
  }

  eventPrefix = 'coaching.self_schedule_purchase.plans_summary';

  trackEvent(key: string) {
    EventTracking.trackKey(`${this.eventPrefix}.${key}`);
  }

  renderPlans() {
    return [
      <Row size="small">
        <Row size="xxsmall">
          <MediaObject gutter="medium" center>
            <Heading level="4" flush>
              Weekly plan
            </Heading>
            <TextWithIcon icon="heart" inline iconSize="medium" iconColor="sunrise">
              <Text size="xxxsmall">Most popular</Text>
            </TextWithIcon>
          </MediaObject>
        </Row>
        <Text size="small">Build on progress with one 40-minute session per week</Text>
      </Row>,
      <Row size="small">
        <Row size="xxsmall">
          <Heading level="4" flush>
            Custom or on-demand
          </Heading>
        </Row>
        <Text size="small">Extend sessions to 60 minutes or schedule them as needed</Text>
      </Row>,
    ];
  }

  renderContent() {
    const ctaText = this.state.isSignedIn ? 'Continue' : 'Continue to log in';
    return (
      <Container size="medium">
        <CoachingHeading size="medium">Try Remind Coaching</CoachingHeading>
        <Row size="xxsmall">
          <Heading level="4">
            Sign up and schedule a <Text weight="bold">free</Text> full-length coaching session
          </Heading>
        </Row>
        <Row size="small">
          <Text color="thunder" size="small">
            No subscriptions or strings attached.
          </Text>
        </Row>

        <Row size="xxsmall">
          <Heading level="4">If you like your free session, you can sign up for more.</Heading>
        </Row>
        <Row size="xxxlarge">
          <Text color="thunder" size="small">
            After you try it out, it's <Text weight="semibold">$40 for a 40-minute session.</Text>
          </Text>
        </Row>

        <Row size="xxxlarge">
          <Button
            eventKey={`${this.eventPrefix}.continue.click`}
            onClick={this.props.onContinue}
            color="koolaid"
            level="primary"
            loading={this.state.loading}>
            {ctaText}
          </Button>
        </Row>

        <Box color="polar-bear" textAlign="left" noShadow noBorder>
          <BoxBody size="xsmall">
            <Row>
              <Text size="small">
                You'll be able to continue with the option that works for you—and make changes any
                time.
              </Text>
            </Row>
            <Display hide="480">
              <Columns2 gutter="medium" divider>
                {this.renderPlans()}
              </Columns2>
            </Display>
            <Display show="480">{this.renderPlans()}</Display>
          </BoxBody>
        </Box>

        {/* We place the image relative to the bottom of the screen so that resizing will still place it in the same area */}
        <Display hide="1024">
          <Image
            src={SwirlyDown}
            style={{
              position: 'relative',
              bottom: '427px',
              right: '45px',
              width: '40px',
              height: '100px',
            }}
          />
        </Display>
      </Container>
    );
  }

  render() {
    return <Container size="medium">{this.renderContent()}</Container>;
  }
}

export default CoachingSelfServeIntroView;
